import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import '../marketer_dashboard_style.css';
import AddTenantRequestWizard from './AddTenantRequestWizard';
import './AddTenantWizard.css';

const REQUEST_TYPE_OPTIONS = [
    { value: 'إضافة مؤسسة', label: 'إضافة مؤسسة' },
    { value: 'تعديل بيانات', label: 'تعديل بيانات' },
    { value: 'تفعيل خدمة', label: 'تفعيل خدمة' },
    { value: 'إلغاء خدمة', label: 'إلغاء خدمة' },
    { value: 'استفسار', label: 'استفسار' },
    { value: 'أخرى', label: 'أخرى' },
];

const MarketerDashboard = ({ onLogout }) => {
    const navigate = useNavigate();
    const [showTenantRequestWizard, setShowTenantRequestWizard] = useState(false);
    const [activeSection, setActiveSection] = useState('tenants');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [marketerName, setMarketerName] = useState('المورد');

    // Tenants state
    const [tenantsData, setTenantsData] = useState([]);
    const [tenantsSearch, setTenantsSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Requests state
    const [requestsData, setRequestsData] = useState([]);
    const [requestsSearch, setRequestsSearch] = useState('');
    const [requestsLoading, setRequestsLoading] = useState(false);
    const [requestsPage, setRequestsPage] = useState(1);
    const [requestsTotalPages, setRequestsTotalPages] = useState(1);
    const [, setRequestsTotalCount] = useState(0);
    const [requestsPendingCount, setRequestsPendingCount] = useState(0);

    // New request form
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [newRequest, setNewRequest] = useState({ type: '', note: '' });
    const [requestSubmitting, setRequestSubmitting] = useState(false);
    const [requestTypeOpen, setRequestTypeOpen] = useState(false);
    const requestTypeRef = useRef(null);

    // Request actions: view / edit / delete
    const [viewRequest, setViewRequest] = useState(null);
    const [editingRequestId, setEditingRequestId] = useState(null);
    const [wizardInitialData, setWizardInitialData] = useState(null);
    const [deleteConfirmRequest, setDeleteConfirmRequest] = useState(null);
    const [deletingRequest, setDeletingRequest] = useState(false);
    const [servicePlans, setServicePlans] = useState([]);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('haseela_marketerToken');
        onLogout?.();
        navigate('/login', { replace: true });
    }, [navigate, onLogout]);

    useEffect(() => {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', 'ar');

        const handleAuthLogout = () => handleLogout();
        window.addEventListener('auth:logout', handleAuthLogout);

        // Try to get marketer name from JWT payload first (fast), then from API
        try {
            const token = localStorage.getItem('haseela_marketerToken');
            if (token) {
                const payloadBase64 = token.split('.')[1];
                const payload = JSON.parse(atob(payloadBase64));
                if (payload.username) setMarketerName(payload.username);
            }
        } catch (_) {}

        axiosInstance.get('/haseela/marketer/profile/')
            .then(res => {
                if (res.data?.username) setMarketerName(res.data.username);
            })
            .catch(() => {});

        axiosInstance.get('/haseela/marketer/services-plans/')
            .then(res => setServicePlans(res.data?.results || res.data || []))
            .catch(() => {});

        return () => window.removeEventListener('auth:logout', handleAuthLogout);
    }, [handleLogout]);


    const loadTenants = useCallback(async (page = 1, search = '', silent = false) => {
        if (!silent) setIsLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            if (search) params.append('search', search);

            const response = await axiosInstance.get(`/haseela/marketer/tenants/?${params.toString()}`);
            const data = response.data;
            setTenantsData(data.results || []);
            setTotalCount(data.count || 0);
            setTotalPages(Math.ceil((data.count || 0) / 10));
            setCurrentPage(page);
        } catch (error) {
            console.error('Error loading tenants:', error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                handleLogout();
            }
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, [handleLogout]);

    const loadRequests = useCallback(async (page = 1, search = '', silent = false) => {
        if (!silent) setRequestsLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            if (search) params.append('search', search);

            const response = await axiosInstance.get(`/haseela/marketer/requests/?${params.toString()}`);
            const data = response.data;
            setRequestsData(data.results || []);
            setRequestsTotalCount(data.count || 0);
            setRequestsPendingCount(data.pending_count || 0);
            setRequestsTotalPages(Math.ceil((data.count || 0) / 10));
            setRequestsPage(page);
        } catch (error) {
            console.error('Error loading requests:', error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                handleLogout();
            }
        } finally {
            if (!silent) setRequestsLoading(false);
        }
    }, [handleLogout]);

    useEffect(() => {
        loadTenants(1, tenantsSearch);
        loadRequests(1, requestsSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-refresh: silently re-fetch tenants & requests so admin actions
    // (approve/reject) reflect here without a manual reload.
    useEffect(() => {
        const interval = setInterval(() => {
            loadTenants(currentPage, tenantsSearch, true);
            loadRequests(requestsPage, requestsSearch, true);
        }, 15000);
        return () => clearInterval(interval);
    }, [currentPage, requestsPage, tenantsSearch, requestsSearch, loadTenants, loadRequests]);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadTenants(1, tenantsSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [tenantsSearch, loadTenants]);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadRequests(1, requestsSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [requestsSearch, loadRequests]);

    const handleSubmitRequest = async (e) => {
        e.preventDefault();
        if (!newRequest.type.trim()) return;
        setRequestSubmitting(true);
        try {
            if (editingRequestId) {
                await axiosInstance.patch(`/haseela/marketer/requests/${editingRequestId}/`, {
                    type: newRequest.type,
                    note: newRequest.note || '',
                });
            } else {
                await axiosInstance.post('/haseela/marketer/requests/', {
                    type: newRequest.type,
                    note: newRequest.note || '',
                });
            }
            setNewRequest({ type: '', note: '' });
            setEditingRequestId(null);
            setShowRequestForm(false);
            loadRequests(editingRequestId ? requestsPage : 1, requestsSearch);
        } catch (error) {
            console.error('Error saving request:', error);
        } finally {
            setRequestSubmitting(false);
        }
    };

    const closeRequestForm = () => {
        setShowRequestForm(false);
        setRequestTypeOpen(false);
        setEditingRequestId(null);
        setNewRequest({ type: '', note: '' });
    };

    const handleRequestTypeSelect = (val) => {
        setRequestTypeOpen(false);
        if (val === 'إضافة مؤسسة') {
            setShowRequestForm(false);
            setShowTenantRequestWizard(true);
            return;
        }
        setNewRequest((prev) => ({ ...prev, type: val }));
    };

    useEffect(() => {
        if (!showRequestForm) setRequestTypeOpen(false);
    }, [showRequestForm]);

    useEffect(() => {
        if (!requestTypeOpen) return;
        const handleClickOutside = (e) => {
            if (requestTypeRef.current && !requestTypeRef.current.contains(e.target)) {
                setRequestTypeOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [requestTypeOpen]);

    const openViewRequest = (req) => {
        let parsed = null;
        if (req.type === 'إضافة مؤسسة' && req.note) {
            try { parsed = JSON.parse(req.note); } catch (_) { parsed = null; }
        }
        setViewRequest({ ...req, parsed });
    };

    const handleEditRequest = (req) => {
        if (req.type === 'إضافة مؤسسة') {
            let parsed = null;
            try { parsed = JSON.parse(req.note); } catch (_) { parsed = null; }
            setWizardInitialData(parsed);
            setEditingRequestId(req.id);
            setShowTenantRequestWizard(true);
        } else {
            setNewRequest({ type: req.type, note: req.note || '' });
            setEditingRequestId(req.id);
            setShowRequestForm(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirmRequest) return;
        setDeletingRequest(true);
        try {
            await axiosInstance.delete(`/haseela/marketer/requests/${deleteConfirmRequest.id}/`);
            setDeleteConfirmRequest(null);
            const nextPage = requestsData.length === 1 && requestsPage > 1 ? requestsPage - 1 : requestsPage;
            loadRequests(nextPage, requestsSearch);
        } catch (error) {
            console.error('Error deleting request:', error);
            alert(error.response?.data?.detail || 'حدث خطأ أثناء حذف الطلب');
        } finally {
            setDeletingRequest(false);
        }
    };

    const isRequestPending = (reqStatus) => !reqStatus || reqStatus === 'pending' || reqStatus === '';

    const getStatusBadge = (reqStatus) => {
        const statusMap = {
            'approved': { label: 'مقبول', className: 'approved' },
            'rejected': { label: 'مرفوض', className: 'rejected' },
            'pending': { label: 'قيد المراجعة', className: 'pending-status' },
        };
        const info = statusMap[reqStatus] || { label: reqStatus || 'قيد المراجعة', className: 'pending-status' };
        return <span className={`status-badge ${info.className}`}>{info.label}</span>;
    };

    const activeTenants = tenantsData.filter(t => t.isactive).length;
    const inactiveTenants = tenantsData.filter(t => !t.isactive).length;

    const handleTenantRequestSubmit = async (wizardData) => {
        setRequestSubmitting(true);
        try {
            if (editingRequestId) {
                await axiosInstance.patch(`/haseela/marketer/requests/${editingRequestId}/`, {
                    type: 'إضافة مؤسسة',
                    note: JSON.stringify(wizardData),
                });
            } else {
                await axiosInstance.post('/haseela/marketer/requests/', {
                    type: 'إضافة مؤسسة',
                    note: JSON.stringify(wizardData),
                });
            }
            const wasEditing = !!editingRequestId;
            setShowTenantRequestWizard(false);
            setShowRequestForm(false);
            setEditingRequestId(null);
            setWizardInitialData(null);
            loadRequests(wasEditing ? requestsPage : 1, requestsSearch);
        } catch (error) {
            console.error('Error saving request:', error);
        } finally {
            setRequestSubmitting(false);
        }
    };

    if (showTenantRequestWizard) {
        return (
            <AddTenantRequestWizard
                onBack={() => { setShowTenantRequestWizard(false); setEditingRequestId(null); setWizardInitialData(null); }}
                onSubmit={handleTenantRequestSubmit}
                isSubmitting={requestSubmitting}
                initialData={wizardInitialData}
                isEdit={!!editingRequestId}
            />
        );
    }

    return (
        <div className="admin-dashboard">
            {/* Mobile overlay (closes sidebar when tapped) */}
            <div
                className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
            ></div>

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-brand">
                        <h2>داشبورد</h2>
                        <span className="brand-subtitle">لوحة المورد</span>
                    </div>
                    <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                        <i className={`fas ${sidebarCollapsed ? 'fa-chevron-left' : 'fa-chevron-right'}`}></i>
                    </button>
                </div>
                <nav className="sidebar-nav">
                    <div className="nav-section">
                        <div
                            className={`nav-item ${activeSection === 'tenants' ? 'active' : ''}`}
                            onClick={() => { setActiveSection('tenants'); setMobileMenuOpen(false); }}
                        >
                            <i className="fas fa-building"></i>
                            <span>المؤسسات</span>
                        </div>
                        <div
                            className={`nav-item ${activeSection === 'requests' ? 'active' : ''}`}
                            onClick={() => { setActiveSection('requests'); setMobileMenuOpen(false); }}
                        >
                            <i className="fas fa-file-alt"></i>
                            <span>الطلبات</span>
                            {requestsPendingCount > 0 && (
                                <span className="nav-badge">{requestsPendingCount}</span>
                            )}
                        </div>
                    </div>
                </nav>
            </aside>

            {/* Main Content */}
            <main className={`main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
                {/* Top Navigation */}
                <header className="top-nav">
                    <div className="nav-container">
                        <div className="nav-left">
                            <button
                                className="mobile-menu-toggle"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                aria-label="القائمة"
                            >
                                <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
                            </button>
                            <div className="logo-section">
                                <img src="/haseela_logo.png" alt="حصيلة" className="nav-logo" />
                                <div className="nav-title">
                                    <span className="nav-title-main">حصيلة</span>
                                    <span className="nav-title-sub">لوحة المورد</span>
                                </div>
                            </div>
                        </div>
                        <div className="nav-right">
                            <button className="nav-btn logout-direct-btn" onClick={handleLogout}>
                                <i className="fas fa-sign-out-alt"></i>
                                <span>تسجيل الخروج</span>
                            </button>
                        </div>
                    </div>
                </header>

                {/* Dashboard Content */}
                <div className="dashboard-content">

                    {/* Welcome Section */}
                    {activeSection !== 'requests' && <div className="welcome-section">
                        <div className="welcome-content">
                            <h2 className="welcome-title">مرحباً بعودتك، {marketerName}!</h2>
                            <p className="welcome-subtitle">إليك ما يحدث في لوحة المورد الخاصة بك اليوم</p>
                        </div>
                        <div className="welcome-actions">
                            <button className="action-btn primary" onClick={() => { setActiveSection('requests'); setShowRequestForm(true); }}>
                                <i className="fas fa-plus"></i>
                                <span>طلب جديد</span>
                            </button>
                        </div>
                    </div>}

                    {/* Stats Grid */}
                    {activeSection !== 'requests' && <div className="stats-grid">
                        <div className="stat-card" onClick={() => setActiveSection('tenants')} style={{ cursor: 'pointer' }}>
                            <div className="stat-icon">
                                <i className="fas fa-building"></i>
                            </div>
                            <div className="stat-content">
                                <h3 className="stat-number">{totalCount}</h3>
                                <p className="stat-label">إجمالي المؤسسات</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <div className="stat-content">
                                <h3 className="stat-number">{activeTenants}</h3>
                                <p className="stat-label">مؤسسات نشطة</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <i className="fas fa-times-circle"></i>
                            </div>
                            <div className="stat-content">
                                <h3 className="stat-number">{inactiveTenants}</h3>
                                <p className="stat-label">مؤسسات غير نشطة</p>
                            </div>
                        </div>
                        <div className="stat-card" onClick={() => setActiveSection('requests')} style={{ cursor: 'pointer' }}>
                            <div className="stat-icon">
                                <i className="fas fa-clock"></i>
                            </div>
                            <div className="stat-content">
                                <h3 className="stat-number">{requestsPendingCount}</h3>
                                <p className="stat-label">طلبات قيد المراجعة</p>
                            </div>
                        </div>
                    </div>}

                    {/* ===== TENANTS SECTION ===== */}
                    {activeSection === 'tenants' && (
                        <div className="data-section">
                            <div className="section-header-bar">
                                <h2><i className="fas fa-building"></i> المؤسسات</h2>
                                <div className="section-actions">
                                    <div className="search-box">
                                        <i className="fas fa-search"></i>
                                        <input
                                            type="text"
                                            placeholder="ابحث عن مؤسسة..."
                                            value={tenantsSearch}
                                            onChange={(e) => setTenantsSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {isLoading ? (
                                <div className="loading-container">
                                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                                    <p>جاري التحميل...</p>
                                </div>
                            ) : tenantsData.length === 0 ? (
                                <div className="empty-state">
                                    <i className="fas fa-inbox fa-3x"></i>
                                    <h3>لا توجد مؤسسات</h3>
                                    <p>قم بإرسال طلب "إضافة مؤسسة" من قسم الطلبات</p>
                                </div>
                            ) : (
                                <>
                                    <div className="data-table-container">
                                        <table className="data-table requests-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>اسم الزبون</th>
                                                    <th>البريد الإلكتروني</th>
                                                    <th>رقم الهاتف</th>
                                                    <th>اسم البزنس</th>
                                                    <th>الحالة</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tenantsData.map((tenant, index) => (
                                                    <tr key={tenant.id}>
                                                        <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                        <td><strong>{tenant.name}</strong></td>
                                                        <td>{tenant.email || '—'}</td>
                                                        <td style={{ direction: 'ltr', textAlign: 'center' }}><span style={{ direction: 'ltr', unicodeBidi: 'embed', display: 'inline-block' }}>{tenant.phone_num}</span></td>
                                                        <td>{tenant.business_names || '—'}</td>
                                                        <td>
                                                            <span className={`status-badge ${tenant.isactive ? 'active' : 'inactive'}`}>
                                                                {tenant.isactive ? 'نشط' : 'غير نشط'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="pagination">
                                            <button
                                                className="pagination-btn"
                                                onClick={() => loadTenants(currentPage - 1, tenantsSearch)}
                                                disabled={currentPage <= 1}
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                                السابق
                                            </button>
                                            <span className="pagination-info">
                                                صفحة {currentPage} من {totalPages}
                                            </span>
                                            <button
                                                className="pagination-btn"
                                                onClick={() => loadTenants(currentPage + 1, tenantsSearch)}
                                                disabled={currentPage >= totalPages}
                                            >
                                                التالي
                                                <i className="fas fa-chevron-left"></i>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ===== REQUESTS SECTION ===== */}
                    {activeSection === 'requests' && (
                        <div className="data-section">
                            <div className="section-header-bar">
                                <h2><i className="fas fa-file-alt"></i> الطلبات</h2>
                                <div className="section-actions">
                                    <div className="search-box">
                                        <i className="fas fa-search"></i>
                                        <input
                                            type="text"
                                            placeholder="ابحث عن طلب..."
                                            value={requestsSearch}
                                            onChange={(e) => setRequestsSearch(e.target.value)}
                                        />
                                    </div>
                                    <button className="btn-add" onClick={() => setShowRequestForm(true)}>
                                        <i className="fas fa-plus"></i>
                                        طلب جديد
                                    </button>
                                </div>
                            </div>

                            {requestsLoading ? (
                                <div className="loading-container">
                                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                                    <p>جاري التحميل...</p>
                                </div>
                            ) : requestsData.length === 0 ? (
                                <div className="empty-state">
                                    <i className="fas fa-file-alt fa-3x"></i>
                                    <h3>لا توجد طلبات</h3>
                                    <p>اضغط على "طلب جديد" لإنشاء طلب</p>
                                </div>
                            ) : (
                                <>
                                    <div className="data-table-container">
                                        <table className="data-table requests-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>نوع الطلب</th>
                                                    <th>الملاحظات</th>
                                                    <th>الحالة</th>
                                                    <th>التاريخ</th>
                                                    <th>الإجراءات</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {requestsData.map((req, index) => (
                                                    <tr key={req.id}>
                                                        <td>{(requestsPage - 1) * 10 + index + 1}</td>
                                                        <td><strong>{req.type}</strong></td>
                                                        <td className="note-cell">
                                                            {req.type === 'إضافة مؤسسة' ? 'بيانات مؤسسة جديدة' : (req.note || '—')}
                                                        </td>
                                                        <td>{getStatusBadge(req.status)}</td>
                                                        <td style={{ direction: 'ltr', textAlign: 'center' }}>
                                                            {new Date(req.created_at).toLocaleDateString('ar-SA', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                            })}
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                <button
                                                                    className="icon-btn view"
                                                                    title="عرض"
                                                                    onClick={() => openViewRequest(req)}
                                                                >
                                                                    <i className="fas fa-eye"></i>
                                                                </button>
                                                                {isRequestPending(req.status) && (
                                                                    <>
                                                                        <button
                                                                            className="icon-btn edit"
                                                                            title="تعديل"
                                                                            onClick={() => handleEditRequest(req)}
                                                                        >
                                                                            <i className="fas fa-edit"></i>
                                                                        </button>
                                                                        <button
                                                                            className="icon-btn delete"
                                                                            title="حذف"
                                                                            onClick={() => setDeleteConfirmRequest(req)}
                                                                        >
                                                                            <i className="fas fa-trash"></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {requestsTotalPages > 1 && (
                                        <div className="pagination">
                                            <button
                                                className="pagination-btn"
                                                onClick={() => loadRequests(requestsPage - 1, requestsSearch)}
                                                disabled={requestsPage <= 1}
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                                السابق
                                            </button>
                                            <span className="pagination-info">
                                                صفحة {requestsPage} من {requestsTotalPages}
                                            </span>
                                            <button
                                                className="pagination-btn"
                                                onClick={() => loadRequests(requestsPage + 1, requestsSearch)}
                                                disabled={requestsPage >= requestsTotalPages}
                                            >
                                                التالي
                                                <i className="fas fa-chevron-left"></i>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {/* New Request Form Modal - at root level so blur covers sidebar */}
            {showRequestForm && (
                <div className="modal-overlay" onClick={closeRequestForm}>
                    <div className={`modal request-modal ${requestTypeOpen ? 'type-menu-open' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><i className={`fas ${editingRequestId ? 'fa-edit' : 'fa-plus-circle'}`}></i> {editingRequestId ? 'تعديل الطلب' : 'طلب جديد'}</h3>
                            <button className="modal-close" onClick={closeRequestForm}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmitRequest}>
                            <div className="form-group request-type-picker" ref={requestTypeRef}>
                                <label>نوع الطلب</label>
                                <div className="request-type-dropdown">
                                    <button
                                        type="button"
                                        className={`request-type-trigger ${requestTypeOpen ? 'open' : ''}`}
                                        onClick={() => setRequestTypeOpen((o) => !o)}
                                        aria-expanded={requestTypeOpen}
                                        aria-haspopup="listbox"
                                    >
                                        <span className={newRequest.type ? '' : 'placeholder'}>
                                            {newRequest.type || 'اختر نوع الطلب...'}
                                        </span>
                                        <i className={`fas fa-chevron-down request-type-chevron ${requestTypeOpen ? 'rotated' : ''}`}></i>
                                    </button>
                                    {requestTypeOpen && (
                                        <ul className="request-type-menu" role="listbox">
                                            {REQUEST_TYPE_OPTIONS.map((opt) => (
                                                <li key={opt.value} role="option" aria-selected={newRequest.type === opt.value}>
                                                    <button
                                                        type="button"
                                                        className={newRequest.type === opt.value ? 'selected' : ''}
                                                        onClick={() => handleRequestTypeSelect(opt.value)}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>ملاحظات</label>
                                <textarea
                                    value={newRequest.note}
                                    onChange={(e) => setNewRequest({ ...newRequest, note: e.target.value })}
                                    placeholder="أضف تفاصيل الطلب هنا..."
                                    rows={4}
                                ></textarea>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={closeRequestForm}>
                                    إلغاء
                                </button>
                                <button type="submit" className="btn-primary" disabled={requestSubmitting || !newRequest.type.trim()}>
                                    {requestSubmitting ? (
                                        <><i className="fas fa-spinner fa-spin"></i> جاري الحفظ...</>
                                    ) : editingRequestId ? (
                                        <><i className="fas fa-save"></i> حفظ التعديلات</>
                                    ) : (
                                        <><i className="fas fa-paper-plane"></i> إرسال الطلب</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Request Detail (View) Modal */}
            {viewRequest && (
                <div className="modal-overlay" onClick={() => setViewRequest(null)}>
                    <div className="modal request-modal" style={{ maxWidth: '720px', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><i className="fas fa-file-alt"></i> تفاصيل الطلب</h3>
                            <button className="modal-close" onClick={() => setViewRequest(null)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                <div><strong>نوع الطلب:</strong> {viewRequest.type}</div>
                                <div><strong>الحالة:</strong> {getStatusBadge(viewRequest.status)}</div>
                                <div><strong>التاريخ:</strong> {new Date(viewRequest.created_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                            </div>

                            {viewRequest.parsed ? (
                                <div>
                                    {viewRequest.parsed.tenant && (
                                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#2871c8' }}><i className="fas fa-user"></i> بيانات المؤسسة</h4>
                                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <div><strong>الاسم:</strong> {viewRequest.parsed.tenant.name}</div>
                                                <div><strong>البريد:</strong> {viewRequest.parsed.tenant.email || '—'}</div>
                                                <div><strong>الهاتف:</strong> <span style={{ direction: 'ltr', unicodeBidi: 'embed', display: 'inline-block' }}>{viewRequest.parsed.tenant.phone_num}</span></div>
                                                <div><strong>الحالة:</strong> {viewRequest.parsed.tenant.isactive ? 'نشط' : 'غير نشط'}</div>
                                            </div>
                                        </div>
                                    )}
                                    {viewRequest.parsed.business && (
                                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#2871c8' }}><i className="fas fa-building"></i> بيانات العمل</h4>
                                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <div><strong>اسم العمل:</strong> {viewRequest.parsed.business.name}</div>
                                                <div><strong>العنوان:</strong> {viewRequest.parsed.business.address}</div>
                                                <div><strong>الهاتف:</strong> <span style={{ direction: 'ltr', unicodeBidi: 'embed', display: 'inline-block' }}>{viewRequest.parsed.business.phone_num}</span></div>
                                                <div><strong>العملة:</strong> {viewRequest.parsed.business.currency}</div>
                                                <div><strong>نسخة التطبيق:</strong> {viewRequest.parsed.business.curr_version}</div>
                                                <div><strong>نوع النسخة:</strong> {viewRequest.parsed.business.version_type}</div>
                                                <div><strong>الحالة:</strong> {viewRequest.parsed.business.isactive ? 'نشط' : 'غير نشط'}</div>
                                            </div>
                                        </div>
                                    )}
                                    {viewRequest.parsed.services_pay && (
                                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#2871c8' }}><i className="fas fa-credit-card"></i> خطة الخدمات</h4>
                                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <div><strong>خطة الخدمة:</strong> {viewRequest.parsed.services_pay.services_plan_name || servicePlans.find(p => p.id === viewRequest.parsed.services_pay.services_plan)?.name || viewRequest.parsed.services_pay.services_plan}</div>
                                                <div><strong>التكلفة:</strong> {viewRequest.parsed.services_pay.final_cost}</div>
                                                <div><strong>تاريخ البداية:</strong> {viewRequest.parsed.services_pay.start_date}</div>
                                                <div><strong>تاريخ النهاية:</strong> {viewRequest.parsed.services_pay.end_date}</div>
                                                <div><strong>حالة الدفع:</strong> {viewRequest.parsed.services_pay.status}</div>
                                                <div><strong>واتساب:</strong> {viewRequest.parsed.services_pay.is_wa ? 'مفعّل' : 'غير مفعّل'}</div>
                                                <div><strong>نسخ احتياطي:</strong> {viewRequest.parsed.services_pay.is_backup ? 'مفعّل' : 'غير مفعّل'}</div>
                                            </div>
                                        </div>
                                    )}
                                    {Array.isArray(viewRequest.parsed.admins) && viewRequest.parsed.admins.length > 0 && (
                                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#2871c8' }}><i className="fas fa-user-shield"></i> المديرين</h4>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead><tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>اسم المستخدم</th>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>رقم الهاتف</th>
                                                </tr></thead>
                                                <tbody>
                                                    {viewRequest.parsed.admins.map((a, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                                            <td style={{ padding: '8px' }}>{a.username}</td>
                                                            <td style={{ padding: '8px', direction: 'ltr' }}>{a.phone_num}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {Array.isArray(viewRequest.parsed.devices) && viewRequest.parsed.devices.length > 0 && (
                                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#2871c8' }}><i className="fas fa-tablet-alt"></i> الأجهزة</h4>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead><tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>الاسم</th>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>العلامة</th>
                                                </tr></thead>
                                                <tbody>
                                                    {viewRequest.parsed.devices.map((d, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                                            <td style={{ padding: '8px' }}>{d.name}</td>
                                                            <td style={{ padding: '8px' }}>{d.tag || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px' }}>
                                    <h4 style={{ marginBottom: '8px', color: '#2871c8' }}>ملاحظات</h4>
                                    <p style={{ margin: 0 }}>{viewRequest.note || 'لا توجد ملاحظات'}</p>
                                </div>
                            )}

                            {isRequestPending(viewRequest.status) && (
                                <div className="modal-actions" style={{ marginTop: '16px' }}>
                                    <button type="button" className="btn-secondary" onClick={() => { const r = viewRequest; setViewRequest(null); handleEditRequest(r); }}>
                                        <i className="fas fa-edit"></i> تعديل
                                    </button>
                                    <button type="button" className="btn-primary" style={{ background: '#dc3545' }} onClick={() => { const r = viewRequest; setViewRequest(null); setDeleteConfirmRequest(r); }}>
                                        <i className="fas fa-trash"></i> حذف
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmRequest && (
                <div className="modal-overlay" onClick={() => !deletingRequest && setDeleteConfirmRequest(null)}>
                    <div className="modal" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 style={{ color: '#dc3545' }}><i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i> تأكيد الحذف</h3>
                            <button className="modal-close" onClick={() => !deletingRequest && setDeleteConfirmRequest(null)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>هل أنت متأكد من حذف هذا الطلب؟</p>
                            <p style={{ margin: '0 0 20px 0', color: '#6c757d', fontSize: '0.92rem' }}>
                                لا يمكن التراجع عن حذف الطلب "{deleteConfirmRequest.type}".
                            </p>
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setDeleteConfirmRequest(null)} disabled={deletingRequest}>
                                    إلغاء
                                </button>
                                <button type="button" className="btn-primary" style={{ background: '#dc3545' }} onClick={handleConfirmDelete} disabled={deletingRequest}>
                                    {deletingRequest ? (
                                        <><i className="fas fa-spinner fa-spin"></i> جاري الحذف...</>
                                    ) : (
                                        <><i className="fas fa-trash"></i> نعم، احذف</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarketerDashboard;
